/**
 *
 * IBM Confidential
 *
 * (C) Copyright IBM Corp. 2023, 2025
 *
 * The source code for this program is not published or otherwise
 * divested of its trade secrets, irrespective of what has been
 * deposited with the U. S. Copyright Office
 *
 * US Government Users Restricted Rights - Use, duplication or
 * disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 *
 **/
import React, { useEffect } from 'react';
import { useFormContext } from 'react-hook-form';
import { FormattedMessage, useIntl } from 'react-intl';

import { useIsNewIA } from '@ibm-watson/shared/modules/ia/hooks/useIsNewIA';
import { FormattedMessageWithLink } from '../../../helpers/linkHelpers';
import { useSMSFeatures } from '../../../hooks/useChannelFeatures';
import { useFormHelpers } from '../../../hooks/useFormHelpers';
import {
  INTELEPEER_SMS_TYPE,
  SMS_CHANNEL_TYPE,
  TWILIO_SMS_TYPE,
  GENESYS_SMS_TYPE,
  CUSTOM_SMS_TYPE
} from '../../../util/channelTypes';
import { QuickSetupComponentProps } from '../../../util/types';
import { TWILIO_COLOR_PRIMARY } from '../../../util/validation/constants';
import { CustomSMSIcon } from '../../icons/CustomSMSIcon';
import { GenesysIcon } from '../../icons/GenesysIcon';
import { IntelepeerIcon } from '../../icons/IntelepeerIcon';
import { TwilioIcon } from '../../icons/TwilioIcon';
import { TwilioAuth } from '../../twilio-auth/TwilioAuth';
import { links } from '../links';
import { FIELDS } from '../schema';
import { CustomAuth } from './CustomAuth';
import { GenesysAuth } from './GenesysAuth';
import { IntelepeerAuth } from './IntelepeerAuth';
import { ConnectProviderStepMessages as messages } from './messages';

export const ConnectProviderStep = ({
  markStepComplete,
  readOnly
}: QuickSetupComponentProps) => {
  const supportedChannels = useSMSFeatures();
  const { formatMessage: t } = useIntl();

  const formMethods = useFormContext();
  const { isValidAndPopulated, getFieldValue } = useFormHelpers(formMethods);

  const selectedProvider = getFieldValue(FIELDS.PROVIDER_TYPE);

  const learnMoreLink = useIsNewIA()
    ? links.learnMoreDeploySmsNewIA
    : links.learnMoreDeploySmsLink;

  // When fields are valid, mark step complete and enable next button
  useEffect(() => {
    let fieldsValid = false;
    if (selectedProvider === INTELEPEER_SMS_TYPE) {
      fieldsValid =
        isValidAndPopulated(FIELDS.INTELEPEER_ACCOUNT_SECRET) &&
        isValidAndPopulated(FIELDS.INTELEPEER_AUTHENTICATION_TOKEN);
    } else if (selectedProvider === TWILIO_SMS_TYPE) {
      fieldsValid =
        isValidAndPopulated(FIELDS.TWILIO_ACCOUNT_SID) &&
        isValidAndPopulated(FIELDS.TWILIO_AUTHENTICATION_TOKEN);
    } else if (selectedProvider === GENESYS_SMS_TYPE) {
      fieldsValid =
        isValidAndPopulated(FIELDS.GENESYS_CLIENT_ID) &&
        isValidAndPopulated(FIELDS.GENESYS_CLIENT_SECRET) &&
        isValidAndPopulated(FIELDS.PROVIDER_URL) &&
        isValidAndPopulated(FIELDS.GENESYS_VERIFICATION_TOKEN);
    } else if (selectedProvider === CUSTOM_SMS_TYPE) {
      fieldsValid =
        isValidAndPopulated(FIELDS.BYOP_VERIFICATION_TOKEN) &&
        isValidAndPopulated(FIELDS.PROVIDER_URL);
    }

    markStepComplete(fieldsValid ?? false);
  }, [isValidAndPopulated, markStepComplete, selectedProvider]);

  return (
    supportedChannels.length > 0 && (
      <>
        <div className="QuickSetup__step-title">
          <FormattedMessage {...messages.title} />
        </div>
        <div className="TabContent__spacer8" />
        <div className="TabContent__subtitle">
          <FormattedMessageWithLink
            {...messages.description}
            url={learnMoreLink}
          />
        </div>
        <div className="TabContent__spacer32" />
        {selectedProvider === TWILIO_SMS_TYPE && (
          <>
            <TwilioIcon
              className="ServiceDesk--twilio"
              channelType={SMS_CHANNEL_TYPE}
              fill={TWILIO_COLOR_PRIMARY}
            />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionTwilio}
                url={links.twilioAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <TwilioAuth
              isQuickSetup
              accountSidField={FIELDS.TWILIO_ACCOUNT_SID}
              authTokenField={FIELDS.TWILIO_AUTHENTICATION_TOKEN}
              readOnly={readOnly}
            />
          </>
        )}
        {selectedProvider === INTELEPEER_SMS_TYPE && (
          <>
            <IntelepeerIcon className="ServiceDesk--intelepeer" />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionIntelepeer}
                url={links.intelepeerAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <IntelepeerAuth isQuickSetup readOnly={readOnly} />
          </>
        )}
        {selectedProvider === GENESYS_SMS_TYPE && (
          <>
            <GenesysIcon className="ServiceDesk--genesys" />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              <FormattedMessage
                {...messages.connectAccount}
                values={{ provider: selectedProvider }}
              />
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              <FormattedMessageWithLink
                {...messages.descriptionGenesys}
                url={links.genesysAccountLink}
              />
            </div>
            <div className="TabContent__spacer32" />
            <GenesysAuth isQuickSetup readOnly={readOnly} />
          </>
        )}
        {selectedProvider === CUSTOM_SMS_TYPE && (
          <>
            <CustomSMSIcon />
            <div className="TabContent__spacer32" />
            <h4 className="productive-heading-02">
              {t(messages.connectBYOPAccount)}
            </h4>
            <div className="TabContent__spacer16" />
            <div className="TabContent__subtitle">
              {t(messages.descriptionCustom)}
            </div>
            <div className="TabContent__spacer32" />
            <CustomAuth isQuickSetup readOnly={readOnly} />
          </>
        )}
      </>
    )
  );
};
